import * as Popover from '@radix-ui/react-popover';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, Ellipsis, MessageCircle, PanelRight, Server } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTokens, MODE_ROLE, MODE_ROLES, type ConversationDTO, type ConversationMode } from '@harness/shared';
import { Spinner } from '../../components/ui';
import { api, download, errorText } from '../../lib/api';
import type { ConversationData } from '../../lib/events';
import { qk } from '../../lib/keys';
import { useAgents, type ProjectDetail } from '../../lib/queries';
import { modeMeta } from '../../lib/modes';
import { toast } from '../../lib/store';
import Composer from './Composer';
import type { CtxTab } from './ContextPanel';
import Messages from './Messages';
import { useNextModel } from './TopBarPickers';

export default function Chat({
  project,
  mode = 'feature',
  convId,
  narrow,
  onToggleCtx,
  onOpenCtx,
  onNewConversation,
}: {
  project: ProjectDetail;
  mode?: ConversationMode;
  convId?: string;
  narrow: boolean;
  onToggleCtx: () => void;
  onOpenCtx: (t: CtxTab) => void;
  onNewConversation: () => Promise<void>;
}) {
  const nav = useNavigate();
  const { model: nextModel, activeAgent, defaultAgent, conv } = useNextModel(project, convId);
  const data = conv.data;
  const busy = data?.conversation.status === 'running' || data?.conversation.status === 'awaiting_approval';

  const used = data?.conversation.tokensUsed ?? 0;
  const limit = nextModel?.contextWindow ?? 200_000;
  const pct = Math.min(100, (used / limit) * 100);
  const serverDown = project.server && project.server.status !== 'online';
  const { data: agents = [] } = useAgents(project.id);
  const modeAgent = mode === 'feature' ? undefined : agents.find((a) => a.role === MODE_ROLE[mode]);

  return (
    <main className="card ws-card chat">
      <header className="chat-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title conv={data?.conversation} />
          <div className="chat-meta">
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
              <Bot className="icon" style={{ fontSize: 13 }} />
              {mode !== 'feature'
                ? `${modeAgent?.name ?? MODE_ROLE[mode]} · read-only`
                : activeAgent
                  ? `${activeAgent.name}${activeAgent.id === defaultAgent?.id ? ' · default agent' : ' · active agent'}`
                  : 'No agent'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }} title={`${used.toLocaleString()} of ${limit.toLocaleString()} tokens in the last request`}>
              Context
              <span className={`meter ${pct > 80 ? 'hot' : ''}`} role="meter" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label="Context used">
                <span style={{ width: `${pct}%` }} />
              </span>
              <b style={{ color: 'var(--color-text)' }}>
                {formatTokens(used)} / {formatTokens(limit)}
              </b>
            </span>
          </div>
        </div>
        {data && <ConversationMenu conv={data.conversation} onDeleted={() => nav(`/p/${project.slug}`)} />}
        {mode !== 'wireframe' && (
          <button className="btn btn-icon btn-secondary" onClick={onToggleCtx} title="Toggle context panel" aria-label="Toggle context panel">
            <PanelRight className="icon" style={{ fontSize: 17 }} />
          </button>
        )}
      </header>

      {serverDown && (
        <div className="banner" role="status">
          <Server className="icon" style={{ fontSize: 16 }} />
          <span style={{ flex: 1 }}>
            <b>Server {project.server!.status}.</b> Tool use is blocked until the connection is back.
            {project.server!.lastSeenAt ? ` Last seen ${new Date(project.server!.lastSeenAt).toLocaleString()}.` : ''}
          </span>
          <button className="btn btn-secondary" onClick={() => nav(`/p/${project.slug}/settings/server`)}>
            Connection settings
          </button>
        </div>
      )}

      <MessageScroller
        convId={convId}
        mode={mode}
        data={data}
        loading={conv.isLoading}
        project={project}
        agentLabel={defaultAgent?.name ?? 'The agent'}
        onOpenCtx={onOpenCtx}
        agents={agents.filter((a) => !MODE_ROLES.includes(a.role))}
      />

      <Composer project={project} mode={mode} convId={convId} busy={!!busy} agents={agents} narrow={narrow} onNeedConversation={onNewConversation} />
    </main>
  );
}

function Title({ conv }: { conv?: ConversationDTO }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  if (!conv) return <div className="chat-title">&nbsp;</div>;
  const save = async () => {
    setEditing(false);
    const t = text.trim();
    if (!t || t === conv.title) return;
    qc.setQueryData<ConversationData>(qk.conversation(conv.id), (d) => (d ? { ...d, conversation: { ...d.conversation, title: t } } : d));
    await api.patch(`/api/conversations/${conv.id}`, { title: t }).catch((e) => toast(errorText(e), 'error'));
  };
  if (editing)
    return (
      <input
        className="input"
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        style={{ minHeight: 28, padding: '2px 8px', fontWeight: 700 }}
        aria-label="Conversation title"
      />
    );
  return (
    <button
      className="chat-title"
      title="Rename"
      onClick={() => {
        setText(conv.title);
        setEditing(true);
      }}
    >
      {conv.title}
    </button>
  );
}

function ConversationMenu({ conv, onDeleted }: { conv: ConversationDTO; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const act = async (fn: () => Promise<unknown>) => {
    setOpen(false);
    try {
      await fn();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className="btn btn-icon btn-secondary" title="Conversation actions" aria-label="Conversation actions">
          <Ellipsis className="icon" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="menu" align="end" sideOffset={6} style={{ width: 230 }}>
          <button className="menu-item" onClick={() => act(() => api.patch(`/api/conversations/${conv.id}`, { pinned: !conv.pinned }).then(() => qc.invalidateQueries({ queryKey: qk.conversations(conv.projectId) })))}>
            {conv.pinned ? 'Unpin' : 'Pin'} conversation
          </button>
          <button className="menu-item" onClick={() => act(async () => download(`/api/conversations/${conv.id}/export?format=md`))}>
            Export as Markdown
          </button>
          <button className="menu-item" onClick={() => act(async () => download(`/api/conversations/${conv.id}/export?format=json`))}>
            Export as JSON
          </button>
          <button
            className="menu-item"
            onClick={() =>
              act(async () => {
                const r = await api.post<{ proposed: number }>(`/api/conversations/${conv.id}/propose-memory`);
                toast(r.proposed ? `${r.proposed} memory fact${r.proposed === 1 ? '' : 's'} proposed · review in Memory` : 'Nothing new worth remembering');
              })
            }
          >
            Propose memory facts
          </button>
          <button
            className="menu-item"
            style={{ color: 'var(--color-accent-700)' }}
            onClick={() =>
              act(async () => {
                if (!confirm(`Delete “${conv.title}”? This can’t be undone.`)) return;
                await api.del(`/api/conversations/${conv.id}`);
                qc.setQueryData<ConversationDTO[]>(qk.conversations(conv.projectId), (l) => l?.filter((c) => c.id !== conv.id));
                onDeleted();
              })
            }
          >
            Delete conversation
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Message column: keeps scroll per conversation, sticks to the bottom while streaming, lazy-loads older pages. */
function MessageScroller({
  convId,
  mode,
  data,
  loading,
  project,
  agentLabel,
  onOpenCtx,
  agents,
}: {
  agents: { id: string; name: string; handle: string }[];
  mode: ConversationMode;
  convId?: string;
  data?: ConversationData;
  loading: boolean;
  project: ProjectDetail;
  agentLabel: string;
  onOpenCtx: (t: CtxTab) => void;
}) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const restored = useRef(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const key = `harness-scroll:${convId}`;

  const lastSig = useMemo(() => {
    const m = data?.messages[data.messages.length - 1];
    if (!m) return '';
    return `${data!.messages.length}:${m.id}:${m.parts.map((p) => (p.type === 'tool_call' ? p.toolCallId : p.text.length)).join(',')}:${m.toolCalls.map((t) => t.status + t.output.length).join(',')}:${data!.gates.map((g) => g.status).join(',')}`;
  }, [data]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !data) return;
    if (!restored.current) {
      restored.current = true;
      const saved = sessionStorage.getItem(key);
      if (saved != null && Number(saved) >= 0) {
        el.scrollTop = Number(saved);
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        return;
      }
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (stick.current) el.scrollTop = el.scrollHeight;
  }, [lastSig, data, key]);

  const loadOlder = async () => {
    if (!convId || !data?.hasMore || loadingOlder || data.oldestSeq == null) return;
    const el = ref.current!;
    const before = el.scrollHeight;
    setLoadingOlder(true);
    try {
      const page = await api.get<{ messages: ConversationData['messages']; hasMore: boolean; oldestSeq: number | null }>(`/api/conversations/${convId}/messages?before=${data.oldestSeq}`);
      qc.setQueryData<ConversationData>(qk.conversation(convId), (d) => (d ? { ...d, messages: [...page.messages, ...d.messages], hasMore: page.hasMore, oldestSeq: page.oldestSeq } : d));
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight - before;
      });
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div
      className="msgs"
      ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        sessionStorage.setItem(key, stick.current ? '-1' : String(el.scrollTop));
        if (el.scrollTop < 200) void loadOlder();
      }}
    >
      <div className="msgs-col">
        {loading && convId && <Spinner />}
        {data?.hasMore && (
          <button className="btn btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={loadOlder}>
            {loadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}
        {(!convId || (data && data.messages.length === 0)) && (
          <div className="success-banner">
            <span className="success-icon">
              <MessageCircle className="icon" />
            </span>
            <div>
              <div className="title-900" style={{ fontSize: 21, lineHeight: 1.2, marginBottom: 4 }}>
                {mode === 'ticket' ? `New ticket for ${project.name}` : mode === 'wireframe' ? `New wireframe for ${project.name}` : mode === 'analysis' ? `Ask about ${project.name}` : `New conversation in ${project.name}`}
              </div>
              {mode !== 'feature' ? (
                <div className="muted">{modeMeta(mode).intro}</div>
              ) : (
              <div className="muted">
                {agentLabel} answers by default.{' '}
                {agents.length > 1 ? (
                  <>
                    Address another agent with {agents.filter((a) => a.name !== agentLabel).map((a, i) => <b key={a.id}>{i ? ' or ' : ''}@{a.handle}</b>)},{' '}
                  </>
                ) : null}
                or start from a Jira task with <b>/feature</b>.
              </div>
              )}
            </div>
          </div>
        )}
        {data && <Messages data={data} project={project} onOpenCtx={onOpenCtx} />}
      </div>
    </div>
  );
}
